import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import {
  Bookmark,
  Copy,
  FolderPlus,
  Loader2,
  Pencil,
  Play,
  Plus,
  Trash2,
  X,
} from 'lucide-react'

import {
  createMyPromptCategory,
  createMyPromptItem,
  deleteMyPromptItem,
  fetchMyPromptCategories,
  fetchMyPromptItems,
  fetchTenantPromptCategories,
  fetchTenantPromptItems,
  updateMyPromptItem,
  type PromptCategory,
  type PromptItem,
} from '@/api/studio-prompt-library'
import { ProtoListTable, ProtoTabs } from '@/features/studio/components/ui-kit'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet'
import { MediaPreviewModal } from '@/features/studio/modals/MediaPreviewModal'
import { PromptFormModal, type PromptFormValues } from '@/features/studio/modals/PromptFormModal'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

type TabKey = 'tenant' | 'mine'

export function PromptLibraryDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<TabKey>('tenant')
  const [categories, setCategories] = useState<PromptCategory[]>([])
  const [items, setItems] = useState<PromptItem[]>([])
  const [activeCategory, setActiveCategory] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [preview, setPreview] = useState<{ url: string; kind: 'image' | 'video'; title: string } | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<PromptItem | null>(null)
  const [formBusy, setFormBusy] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<PromptItem | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryBusy, setNewCategoryBusy] = useState(false)

  const loadData = useCallback(async () => {
    if (!open) return
    setLoading(true)
    setError('')
    try {
      if (tab === 'tenant') {
        const [cats, list] = await Promise.all([
          fetchTenantPromptCategories(),
          fetchTenantPromptItems({
            category_code: activeCategory || undefined,
            limit: 100,
          }),
        ])
        setCategories(cats.items ?? [])
        setItems(list.items ?? [])
      } else {
        const [cats, list] = await Promise.all([
          fetchMyPromptCategories(),
          fetchMyPromptItems({
            category_id: activeCategory || undefined,
            limit: 100,
          }),
        ])
        setCategories(cats.items ?? [])
        setItems(list.items ?? [])
      }
    } catch (e) {
      setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'promptLibrary.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [activeCategory, open, t, tab])

  useEffect(() => {
    if (!open) return
    void loadData()
  }, [loadData, open])

  useEffect(() => {
    if (!open) return
    setActiveCategory('')
    setError('')
    setExpandedId(null)
    setCategoryModalOpen(false)
    setNewCategoryName('')
  }, [open, tab])

  const categoryChips = useMemo(() => {
    const all = [{ id: '', name: t('promptLibrary.allCategories') }]
    return [...all, ...categories.map((c) => ({ id: c.id, name: c.name }))]
  }, [categories, t])

  async function copyPrompt(item: PromptItem) {
    try {
      await navigator.clipboard.writeText(item.content)
      setCopiedId(item.id)
      window.setTimeout(() => setCopiedId(null), 2000)
    } catch {
      setError(t('promptLibrary.copyFailed'))
    }
  }

  async function submitForm(values: PromptFormValues) {
    setFormBusy(true)
    setError('')
    try {
      const payload = {
        category_id: values.category_id || undefined,
        name: values.name.trim(),
        content: values.content.trim(),
        remark: values.remark.trim(),
        effect_media_url: values.effect_media_url,
        effect_media_kind: values.effect_media_kind,
        sort_order: values.sort_order,
      }
      if (editing) {
        await updateMyPromptItem(editing.id, payload)
      } else {
        await createMyPromptItem(payload)
      }
      setFormOpen(false)
      setEditing(null)
      await loadData()
    } catch (e) {
      setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'promptLibrary.saveFailed')))
    } finally {
      setFormBusy(false)
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      await deleteMyPromptItem(deleteTarget.id)
      setDeleteTarget(null)
      await loadData()
    } catch (e) {
      setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'promptLibrary.deleteFailed')))
    } finally {
      setDeleteBusy(false)
    }
  }

  async function createCategory() {
    const name = newCategoryName.trim()
    if (!name) return
    setNewCategoryBusy(true)
    setError('')
    try {
      await createMyPromptCategory({ name })
      setNewCategoryName('')
      setCategoryModalOpen(false)
      await loadData()
    } catch (e) {
      setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'promptLibrary.saveFailed')))
    } finally {
      setNewCategoryBusy(false)
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="flex w-full max-w-4xl flex-col gap-0 p-0 sm:max-w-4xl"
        >
          <SheetHeader className="flex shrink-0 flex-row items-center justify-between gap-2 border-b px-4 py-2.5 text-left">
            <div className="flex min-w-0 items-center gap-2">
              <Bookmark className="h-4 w-4 shrink-0 text-indigo-400" />
              <SheetTitle className="truncate text-base font-semibold">{t('promptLibrary.title')}</SheetTitle>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {tab === 'mine' ? (
                <>
                  <Button
                    type="button"
                    variant="accent"
                    size="sm"
                    title={t('promptLibrary.createPrompt')}
                    onClick={() => {
                      setEditing(null)
                      setFormOpen(true)
                    }}
                    className="gap-1 px-2.5 py-1.5 text-xs"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{t('promptLibrary.createPrompt')}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    title={t('promptLibrary.createCategory')}
                    onClick={() => {
                      setNewCategoryName('')
                      setCategoryModalOpen(true)
                    }}
                    className="gap-1 px-2.5 py-1.5 text-xs"
                  >
                    <FolderPlus className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{t('promptLibrary.createCategory')}</span>
                  </Button>
                </>
              ) : null}
              <Button type="button" variant="ghost" size="icon-sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="shrink-0 border-b px-4 py-2">
            <ProtoTabs
              tabs={[
                { id: 'tenant', label: t('promptLibrary.tabTenant') },
                { id: 'mine', label: t('promptLibrary.tabMine') },
              ]}
              active={tab}
              onChange={(v) => setTab(v)}
            />
          </div>

          <div className="studio-scrollbar-hidden shrink-0 border-b px-4 py-2">
            <div className="flex flex-wrap gap-1.5">
              {categoryChips.map((chip) => (
                <Button
                  key={chip.id || 'all'}
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => setActiveCategory(chip.id)}
                  className={clsx(
                    'rounded-full px-2.5 py-1 text-[11px]',
                    activeCategory === chip.id
                      ? 'bg-indigo-500/20 text-indigo-200'
                      : 'bg-white/[0.04] text-zinc-400 hover:text-zinc-200',
                  )}
                >
                  {chip.name}
                </Button>
              ))}
            </div>
          </div>

          {error ? (
            <div className="shrink-0 border-b border-rose-500/20 bg-rose-500/5 px-5 py-2">
              <p className="text-xs text-rose-400">{error}</p>
            </div>
          ) : null}

          <div className="studio-scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {loading ? (
              <div className="flex justify-center py-16 text-zinc-500">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Bookmark className="mb-3 h-8 w-8 text-zinc-700" />
                <p className="text-sm text-zinc-500">
                  {tab === 'tenant' ? t('promptLibrary.tenantEmpty') : t('promptLibrary.mineEmpty')}
                </p>
              </div>
            ) : (
              <ProtoListTable>
                <table className="w-full min-w-[680px] table-fixed text-left text-xs">
                  <colgroup>
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '52%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '12%' }} />
                  </colgroup>
                  <thead className="border-b border-white/[0.06] bg-[#12121a] text-[11px] text-zinc-500">
                    <tr>
                      <th className="px-3 py-2">{t('promptLibrary.colName')}</th>
                      <th className="px-3 py-2">{t('promptLibrary.colPrompt')}</th>
                      <th className="px-3 py-2">{t('promptLibrary.colRemark')}</th>
                      <th className="px-3 py-2">{t('promptLibrary.colThumb')}</th>
                      <th className="px-3 py-2">{t('promptLibrary.colAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <PromptTableRow
                        key={item.id}
                        item={item}
                        tab={tab}
                        expanded={expandedId === item.id}
                        copied={copiedId === item.id}
                        onToggleExpand={() => setExpandedId((id) => (id === item.id ? null : item.id))}
                        onCopy={() => void copyPrompt(item)}
                        onEdit={() => {
                          setEditing(item)
                          setFormOpen(true)
                        }}
                        onDelete={() => setDeleteTarget(item)}
                        onPreview={() => {
                          if (!item.effect_media_url || !item.effect_media_kind) return
                          setPreview({
                            url: item.effect_media_url,
                            kind: item.effect_media_kind as 'image' | 'video',
                            title: item.name,
                          })
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </ProtoListTable>
            )}
          </div>

        </SheetContent>
      </Sheet>

      {categoryModalOpen ? (
        <Dialog open={categoryModalOpen} onOpenChange={(next) => !next && setCategoryModalOpen(false)}>
          <DialogContent className="max-w-sm gap-0 p-5" showCloseButton={false}>
            <DialogHeader className="text-left">
              <DialogTitle className="text-sm font-semibold">{t('promptLibrary.createCategory')}</DialogTitle>
            </DialogHeader>
            <Input
              autoFocus
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder={t('promptLibrary.categoryNamePlaceholder')}
              className="mt-4"
              onKeyDown={(e) => {
                if (e.key === 'Enter') void createCategory()
              }}
            />
            <DialogFooter className="mt-4 gap-2 sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setCategoryModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                disabled={newCategoryBusy || !newCategoryName.trim()}
                onClick={() => void createCategory()}
              >
                {newCategoryBusy ? t('canvas.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <MediaPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? ''}
        kind={preview?.kind ?? 'image'}
        title={preview?.title}
      />

      <PromptFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
        initial={editing}
        categories={categories}
        busy={formBusy}
        onSubmit={submitForm}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('promptLibrary.deletePromptTitle')}
        message={t('promptLibrary.deletePromptConfirm', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}

function PromptTableRow({
  item,
  tab,
  expanded,
  copied,
  onToggleExpand,
  onCopy,
  onEdit,
  onDelete,
  onPreview,
}: {
  item: PromptItem
  tab: TabKey
  expanded: boolean
  copied: boolean
  onToggleExpand: () => void
  onCopy: () => void
  onEdit: () => void
  onDelete: () => void
  onPreview: () => void
}) {
  const { t } = useTranslation()
  const nameLabel = item.name || t('promptLibrary.uncategorized')
  const hasMedia = Boolean(item.effect_media_url && item.effect_media_kind)
  const isVideo = item.effect_media_kind === 'video'
  const remark = item.remark?.trim() ?? ''

  return (
    <tr className="border-b border-white/[0.04] hover:bg-white/[0.02]">
      <td className="px-3 py-2 font-medium leading-snug text-zinc-300">{nameLabel}</td>
      <td className="px-3 py-2 text-zinc-300">
        <Button
          type="button"
          variant="ghost"
          onClick={onToggleExpand}
          className="h-auto w-full justify-start p-0 text-left text-xs leading-relaxed hover:text-zinc-100"
        >
          <span
            className={clsx(
              'block whitespace-pre-wrap',
              expanded ? 'max-h-48 overflow-y-auto studio-scrollbar-thin' : 'line-clamp-2',
            )}
          >
            {item.content}
          </span>
        </Button>
      </td>
      <td className="px-3 py-2 text-zinc-500">
        {remark ? (
          <span className={clsx('block whitespace-pre-wrap text-xs leading-relaxed', !expanded && 'line-clamp-2')}>
            {remark}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-2">
        {hasMedia ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onPreview}
            className="relative block h-8 w-11 overflow-hidden rounded border border-white/[0.06] bg-[#0e0e16] p-0 hover:border-indigo-500/40"
          >
            {isVideo ? (
              <>
                <video
                  src={resolveMediaDisplayUrl(item.effect_media_url)}
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
                  <Play className="h-2.5 w-2.5 text-white" fill="currentColor" />
                </span>
              </>
            ) : (
              <img
                src={resolveMediaDisplayUrl(item.effect_media_url)}
                alt=""
                className="h-full w-full object-cover"
              />
            )}
          </Button>
        ) : (
          <span className="text-zinc-600">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-nowrap items-center gap-0.5">
          {tab === 'mine' ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                title={t('common.edit')}
                onClick={onEdit}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                title={t('common.delete')}
                onClick={onDelete}
                className="hover:bg-rose-500/10 hover:text-rose-300"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            title={copied ? t('promptLibrary.copied') : t('promptLibrary.applyPrompt')}
            onClick={onCopy}
            className={clsx(
              copied
                ? 'text-emerald-400 hover:bg-emerald-500/10'
                : 'text-indigo-300 hover:bg-indigo-500/10 hover:text-indigo-200',
            )}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  )
}
